import React, { createContext, useContext, useEffect, useState } from "react";

const DEFAULT_HEADER = { title: "", subtitle: "", actions: null };

const PageHeaderValueContext = createContext(DEFAULT_HEADER);
const PageHeaderSetterContext = createContext(() => {});

/**
 * Wraps <Outlet/> in Layout.jsx so the single, centralized <Topbar/> can
 * read whatever the currently-rendered page has registered via
 * `usePageHeader`, without each page rendering its own <Topbar/>.
 */
export function PageHeaderProvider({ children }) {
  const [header, setHeader] = useState(DEFAULT_HEADER);
  return (
    <PageHeaderSetterContext.Provider value={setHeader}>
      <PageHeaderValueContext.Provider value={header}>
        {children}
      </PageHeaderValueContext.Provider>
    </PageHeaderSetterContext.Provider>
  );
}

/** Read by Layout.jsx's single Topbar instance. */
export function usePageHeaderValue() {
  return useContext(PageHeaderValueContext);
}

/**
 * Called by a page to set the title/subtitle/actions shown in the shared
 * Topbar. Pass `enabled=false` (e.g. when a page is rendered "embedded"
 * inside another surface) to skip registering entirely.
 */
export function usePageHeader({ title = "", subtitle = "", actions = null }, enabled = true) {
  const setHeader = useContext(PageHeaderSetterContext);
  useEffect(() => {
    if (!enabled) return;
    setHeader({ title, subtitle, actions });
    return () => setHeader(DEFAULT_HEADER);
  }, [enabled, title, subtitle, actions, setHeader]);
}
